/**
 * Planilhas > "Google Drive (recomendado)".
 *
 * O backend lê a planilha do Legado direto do Drive com uma conta de serviço
 * do Google: a unidade cadastra aqui a chave JSON dessa conta (guardada
 * cifrada no servidor, nunca exibida de volta), compartilha o arquivo com o
 * e-mail dela (Leitor) e cola o link. É o que faz celular e tablet abrirem as
 * telas do Legado sem escolher arquivo.
 *
 * - `planilha_legado:configurar`: chave da conta de serviço, e-mail para
 *   compartilhar, link/ID, chave Ativo, Salvar, Testar conexão e o estado.
 * - só `planilha_legado:view`: apenas o estado ("carregada do Drive, versão de").
 */
import React, { useCallback, useEffect, useState } from "react";

import AsyncButton from "../../../components/AsyncButton";
import BotaoCopiar from "../../../components/BotaoCopiar";
import { useAuth } from "../../../context/AuthContext";
import { FONTE_SERVIDOR, useDados } from "../../../context/DadosContext";
import { useToast } from "../../../context/ToastContext";
import {
  configuracao as buscarConfiguracao,
  extrairIdArquivoDrive,
  salvarConfiguracao,
  status as buscarStatus,
  testarConfiguracao,
} from "../../../services/planilhaLegado";
import { dataLocalDe } from "../../../utils/dinheiro";
import { formatarTamanho } from "../../../utils/documentos";

export const PERMISSAO_VER = "planilha_legado:view";
export const PERMISSAO_CONFIGURAR = "planilha_legado:configurar";

const OPCOES_DATA_HORA = { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" };

/** ISO com fuso -> "22/09 10:40" no horário local. */
export function formatarVersao(iso) {
  const data = dataLocalDe(iso);
  if (!data) return null;
  return data.toLocaleString("pt-BR", OPCOES_DATA_HORA).replace(",", "");
}

const estiloAviso = (tom) => ({
  padding: "8px 12px",
  borderRadius: 8,
  fontSize: 13,
  lineHeight: 1.45,
  background: `var(--${tom}-bg)`,
  color: `var(--${tom}-text)`,
  border: `1px solid var(--${tom}-border)`,
});

const estiloTexto = { fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5 };
const estiloTextoFraco = { fontSize: 12, color: "var(--ink-3)", lineHeight: 1.5 };

/** Estado da planilha NESTE aparelho (vem do DadosContext). */
function EstadoNoAparelho() {
  const { fonte, atualizadoEm, erroServidor } = useDados() || {};
  const versao = formatarVersao(atualizadoEm);

  return (
    <>
      {fonte === FONTE_SERVIDOR ? (
        <div style={{ ...estiloTexto, color: "var(--success-text)" }}>
          Planilha carregada do Google Drive{versao ? `, versão de ${versao}` : ""}.
        </div>
      ) : (
        <div style={estiloTextoFraco}>
          Neste aparelho a planilha ainda não veio do Google Drive — vale o arquivo abaixo.
        </div>
      )}
      {erroServidor ? <div style={estiloAviso("warning")}>{erroServidor}</div> : null}
    </>
  );
}

function EstadoDoServidor({ statusDrive }) {
  if (!statusDrive) return null;
  const { arquivo, verificado_em: verificadoEm, erro } = statusDrive;
  return (
    <div style={{ display: "grid", gap: 4 }}>
      {arquivo ? (
        <div style={estiloTexto}>
          <strong>{arquivo.nome}</strong> · {formatarTamanho(arquivo.tamanho)} · modificado em{" "}
          {formatarVersao(arquivo.modificado_em) || "—"}
        </div>
      ) : null}
      {verificadoEm ? (
        <div style={estiloTextoFraco}>Verificado no Drive em {formatarVersao(verificadoEm)}.</div>
      ) : null}
      {erro ? <div style={estiloAviso("danger")}>{erro}</div> : null}
    </div>
  );
}

function ContaDeServico({ email, onErroCopiar }) {
  return (
    <>
      <div style={estiloTexto}>Compartilhe a planilha com este e-mail:</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
        <code style={{ fontSize: 13, overflowWrap: "anywhere" }}>{email}</code>
        <BotaoCopiar texto={email} onErro={onErroCopiar} aria-label="Copiar e-mail da conta de serviço" />
      </div>
      <ol style={{ ...estiloTexto, margin: 0, paddingLeft: 18 }}>
        <li>No Google Drive, abra o menu da planilha e clique em Compartilhar.</li>
        <li>Adicione o e-mail acima como <strong>Leitor</strong>.</li>
        <li>Copie o link da planilha e cole no campo abaixo.</li>
      </ol>
    </>
  );
}

/**
 * Chave JSON da conta de serviço. Com chave cadastrada, mostra só o e-mail
 * dela e o botão "Trocar chave"; o conteúdo nunca volta do servidor.
 */
function ChaveDaConta({ cadastrada, email, valor, onChange, onErroArquivo }) {
  const [trocando, setTrocando] = useState(false);
  const mostrarCampo = !cadastrada || trocando;

  const lerArquivo = (evento) => {
    const arquivo = evento.target.files?.[0];
    evento.target.value = "";
    if (!arquivo) return;
    const leitor = new FileReader();
    leitor.onload = () => onChange(String(leitor.result || ""));
    leitor.onerror = () => onErroArquivo();
    leitor.readAsText(arquivo);
  };

  if (!mostrarCampo) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ ...estiloTexto, color: "var(--success-text)" }}>
          Chave da conta de serviço cadastrada{email ? ` (${email})` : ""}.
        </span>
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => setTrocando(true)}>
          Trocar chave
        </button>
      </div>
    );
  }

  return (
    <div className="input-group">
      <label className="input-label" htmlFor="drive-chave-json">
        Chave da conta de serviço (arquivo JSON do Google Cloud)
      </label>
      <textarea
        id="drive-chave-json"
        className="input"
        rows={4}
        autoComplete="off"
        spellCheck={false}
        placeholder='Cole aqui o conteúdo do arquivo .json, começando por {"type": "service_account", …'
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 12, resize: "vertical" }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <label className="btn btn--secondary btn--sm" style={{ cursor: "pointer" }}>
          Escolher arquivo .json
          <input type="file" accept=".json,application/json" onChange={lerArquivo} hidden />
        </label>
        {cadastrada ? (
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => {
              onChange("");
              setTrocando(false);
            }}
          >
            Manter a chave atual
          </button>
        ) : null}
        <span style={estiloTextoFraco}>Fica guardada cifrada no servidor e não é mostrada de novo.</span>
      </div>
    </div>
  );
}

function ConfiguracaoDrive() {
  const { request } = useAuth();
  const toast = useToast();
  const { sincronizarComServidor } = useDados() || {};

  const [config, setConfig] = useState(null);
  const [statusDrive, setStatusDrive] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState(null);
  const [campo, setCampo] = useState("");
  const [chave, setChave] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [resultadoTeste, setResultadoTeste] = useState(null);
  const [erroSalvar, setErroSalvar] = useState(null);

  const recarregarStatus = useCallback(async () => {
    try {
      setStatusDrive(await buscarStatus(request));
    } catch (err) {
      setStatusDrive({ erro: err?.message || "Não foi possível consultar o estado." });
    }
  }, [request]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      const [cfg] = await Promise.allSettled([buscarConfiguracao(request), recarregarStatus()]);
      if (!vivo) return;
      if (cfg.status === "fulfilled") {
        setConfig(cfg.value);
        setCampo(cfg.value?.drive_arquivo_id || "");
        setAtivo(cfg.value?.ativo ?? true);
      } else {
        setErroCarga(cfg.reason?.message || "Não foi possível carregar a configuração.");
      }
      setCarregando(false);
    })();
    return () => {
      vivo = false;
    };
  }, [request, recarregarStatus]);

  const email = config?.conta_servico_email || statusDrive?.conta_servico_email || null;
  const integracaoDisponivel = statusDrive?.integracao_disponivel ?? Boolean(email);
  const chaveCadastrada = Boolean(config?.credencial_cadastrada);
  const campoAlterado = campo.trim() !== (config?.drive_arquivo_id || "") || Boolean(chave.trim());

  // Erro tratado aqui (inline + toast) em vez de deixar o AsyncButton
  // relançar: o 400 "link não reconhecido" precisa ficar visível junto do campo.
  const salvar = async () => {
    const id = extrairIdArquivoDrive(campo);
    setCampo(id);
    setErroSalvar(null);
    setResultadoTeste(null);
    let salvo;
    try {
      salvo = await salvarConfiguracao(request, { driveLinkOuId: id, ativo, credencialJson: chave });
    } catch (err) {
      const mensagem = err?.message || "Não foi possível salvar a configuração.";
      setErroSalvar(mensagem);
      toast.error(mensagem);
      return;
    }
    setConfig(salvo);
    setChave("");
    setCampo(salvo?.drive_arquivo_id ?? id);
    setAtivo(salvo?.ativo ?? ativo);
    toast.success("Configuração do Google Drive salva.");
    await recarregarStatus();
    // Este aparelho troca de fonte já, sem esperar a próxima verificação.
    if (sincronizarComServidor) sincronizarComServidor({ forcar: true });
  };

  const testar = async () => {
    setResultadoTeste(null);
    try {
      const r = await testarConfiguracao(request);
      const nome = r?.arquivo?.nome;
      const mensagem = nome ? `Conexão com o Drive OK: ${nome}.` : "Conexão com o Drive OK.";
      setResultadoTeste({ ok: true, mensagem });
      toast.success(mensagem);
    } catch (err) {
      const mensagem = err?.message || "Não foi possível ler o arquivo no Drive.";
      setResultadoTeste({ ok: false, mensagem });
      toast.error(mensagem);
    }
    await recarregarStatus();
  };

  if (carregando) {
    return <span style={estiloTextoFraco}>Carregando…</span>;
  }

  return (
    <>
      {erroCarga ? <div style={estiloAviso("danger")}>{erroCarga}</div> : null}

      {!integracaoDisponivel ? (
        <div style={estiloAviso("warning")}>
          Cadastre a chave da conta de serviço do Google (o arquivo JSON baixado no Google Cloud).
          Enquanto isso, cada aparelho usa a planilha escolhida nele.
        </div>
      ) : null}

      <ChaveDaConta
        cadastrada={chaveCadastrada}
        email={config?.conta_servico_email || null}
        valor={chave}
        onChange={setChave}
        onErroArquivo={() => toast.error("Não foi possível ler o arquivo. Abra o .json e cole o conteúdo.")}
      />

      {integracaoDisponivel && email ? (
        <ContaDeServico
          email={email}
          onErroCopiar={() => toast.error("Não foi possível copiar. Selecione o e-mail e copie manualmente.")}
        />
      ) : null}

      <div className="input-group">
        <label className="input-label" htmlFor="drive-link-ou-id">
          Link ou ID do arquivo no Drive
        </label>
        <input
          id="drive-link-ou-id"
          className="input"
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder="https://drive.google.com/file/d/…"
          value={campo}
          onChange={(e) => setCampo(e.target.value)}
          onBlur={() => setCampo(extrairIdArquivoDrive(campo))}
        />
      </div>
      {erroSalvar ? <div style={estiloAviso("danger")}>{erroSalvar}</div> : null}

      <div className="switch-row" style={{ borderTop: 0, paddingTop: 0 }}>
        <div className="sr-txt">
          <div className="t">Ativo</div>
          <div className="d">Desligado, os aparelhos voltam a usar a planilha escolhida em cada um.</div>
        </div>
        <div
          className={`switch ${ativo ? "is-on" : ""}`}
          role="switch"
          aria-checked={ativo}
          aria-label="Ativo"
          tabIndex={0}
          onClick={() => setAtivo((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setAtivo((v) => !v);
            }
          }}
        />
      </div>

      {resultadoTeste ? (
        <div style={estiloAviso(resultadoTeste.ok ? "success" : "danger")}>{resultadoTeste.mensagem}</div>
      ) : null}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <AsyncButton
          variant="secondary"
          onClick={testar}
          silentSuccess
          silentError
          disabled={!config?.drive_arquivo_id || campoAlterado}
          title={campoAlterado ? "Salve o link antes de testar." : undefined}
        >
          Testar conexão
        </AsyncButton>
        <AsyncButton
          variant="primary"
          onClick={salvar}
          silentSuccess
          disabled={!campo.trim()}
        >
          Salvar
        </AsyncButton>
      </div>

      <EstadoDoServidor statusDrive={statusDrive} />
      <EstadoNoAparelho />
    </>
  );
}

export default function PlanilhaGoogleDrive() {
  const { hasPermission } = useAuth();
  const podeConfigurar = hasPermission(PERMISSAO_CONFIGURAR);
  const podeVer = hasPermission(PERMISSAO_VER);
  if (!podeConfigurar && !podeVer) return null;

  return (
    <div className="set-row">
      <div>
        <div className="set-row__lbl">Google Drive (recomendado)</div>
        <div className="set-row__desc">
          O servidor lê a planilha principal direto do Google Drive: celular e tablet abrem o
          Legado sem escolher arquivo, e a versão nova chega sozinha.
        </div>
      </div>
      <div className="set-row__control">
        {podeConfigurar ? <ConfiguracaoDrive /> : <EstadoNoAparelho />}
      </div>
    </div>
  );
}
